import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, ImagePlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { SPORTS, plur } from "../../lib/localize";
import ExpandableText from "./ExpandableText";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";

const EMPTY = {
  slug: "", title_fr: "", title_en: "", desc_fr: "", desc_en: "",
  meta_fr: "", meta_en: "", keywords: "", category: "", image: "", published: true, sport: "",
  seo_keywords: "", seo_keywords_en: "",
};

const sportLabel = (id) => SPORTS.find((s) => s.id === id)?.fr || "";

// Champ de formulaire (hors composant : évite le remontage/perte de focus à chaque frappe)
function OccField({ label, k, textarea = false, placeholder = "", readOnly = false, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm text-ivory/70 mb-1.5">{label}</label>
      {textarea ? (
        <textarea rows={3} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
          className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
      ) : (
        <input value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly} data-testid={testid}
          className={`w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`} />
      )}
    </div>
  );
}

// Bouton de suppression avec confirmation (même comportement que celui de l'admin)
const DeleteConfirmButton = ({ testid, label, onDelete }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid={testid} className="text-ivory/70 hover:text-red-400 transition-colors duration-300">
        <Trash2 className="w-4 h-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-surface border-gold/20 text-ivory">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-serif text-2xl">Confirmer la suppression</AlertDialogTitle>
        <AlertDialogDescription className="text-ivory/60">{label} Cette action est irréversible.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="border-ivory/20 text-ivory/70 hover:bg-ivory/5">Annuler</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onDelete} className="bg-red-500 text-white hover:bg-red-400">
          Supprimer
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Onglet admin « Occasions » : CRUD des pages d'atterrissage SEO (/occasions/...).
// keywords est saisi en texte séparé par des virgules, converti en liste à l'envoi.
export default function AdminOccasions() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);   // null = fermé, {} = formulaire ouvert
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = () => api.get("/admin/occasions").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY, _isNew: true });
  const openEdit = (o) => setEditing({ ...o, keywords: (o.keywords || []).join(", "), _isNew: false, _originalSlug: o.slug });

  const payload = () => ({
    ...editing,
    slug: editing._originalSlug || editing.slug,
    keywords: editing.keywords.split(",").map((k) => k.trim()).filter(Boolean),
  });

  const save = async () => {
    setSaving(true);
    try {
      if (editing._isNew) await api.post("/admin/occasions", payload());
      else await api.patch(`/admin/occasions/${editing._originalSlug}`, payload());
      toast.success("Occasion enregistrée");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (o, v) => {
    try {
      await api.patch(`/admin/occasions/${o.slug}`, { ...o, published: v });
      setItems((prev) => prev.map((x) => (x.slug === o.slug ? { ...x, published: v } : x)));
      toast.success(v ? `« ${o.title_fr} » publiée` : `« ${o.title_fr} » masquée`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/image", form);
      setEditing((prev) => ({ ...prev, image: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  if (!items) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;

  const fieldProps = (k) => ({
    value: editing?.[k] ?? "",
    onChange: (e) => setEditing((s) => ({ ...s, [k]: e.target.value })),
    testid: `occasion-form-${k.replace(/_/g, "-")}`,
  });

  return (
    <div data-testid="admin-occasions">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-ivory/60">Pages d'atterrissage SEO visibles sur /occasions — chaque page regroupe automatiquement les produits correspondant à ses mots-clés et à son sport.</p>
        <div className="flex items-center gap-2 shrink-0 ml-4">
          <button
            data-testid="occasions-seed-sports-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/admin/occasions/seed-sports");
                toast.success(`Pages sports : ${plur(data.created, "créée")}, ${plur(data.tagged, "existante")} ${data.tagged > 1 ? "associées" : "associée"} à un sport`);
                load();
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail));
              }
            }}
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
            <Plus className="w-4 h-4" /> Pages par sport
          </button>
          <button onClick={openNew} data-testid="occasion-new"
            className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
            <Plus className="w-4 h-4" /> Nouvelle occasion
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((o) => (
          <div key={o.slug} data-testid={`occasion-row-${o.slug}`}
            className="flex items-center gap-4 bg-surface border border-ivory/10 rounded-xl p-4">
            {o.image && <img src={o.image} alt="" className="w-14 h-14 object-contain bg-white rounded-lg shrink-0" />}
            <div className="flex-1 min-w-0">
              <ExpandableText text={o.title_fr} testId={`occasion-title-${o.slug}`} className="block font-semibold text-sm">
                {o.title_fr}
                {o.sport && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-gold bg-gold/10 border border-gold/30 rounded-full px-2 py-0.5 align-middle"
                    data-testid={`occasion-sport-${o.slug}`}>
                    {sportLabel(o.sport) || o.sport}
                  </span>
                )}
              </ExpandableText>
              <ExpandableText text={`/occasions/${o.slug} · mots-clés : ${(o.keywords || []).join(", ") || "—"}`}
                testId={`occasion-meta-${o.slug}`} className="block text-xs text-ivory/60" />
            </div>
            <a href={`/occasions/${o.slug}`} target="_blank" rel="noreferrer" data-testid={`occasion-view-${o.slug}`}
              className="text-ivory/60 hover:text-gold transition-colors duration-300" title="Voir la page">
              <ExternalLink className="w-4 h-4" />
            </a>
            <Switch checked={!!o.published} onCheckedChange={(v) => togglePublished(o, v)}
              data-testid={`occasion-published-${o.slug}`} />
            <button onClick={() => openEdit(o)} data-testid={`occasion-edit-${o.slug}`}
              className="text-ivory/60 hover:text-gold transition-colors duration-300">
              <Pencil className="w-4 h-4" />
            </button>
            <DeleteConfirmButton
              testid={`occasion-delete-${o.slug}`}
              label={`Supprimer la page « ${o.title_fr} » (/occasions/${o.slug}) ? Elle disparaîtra du site et du sitemap.`}
              onDelete={async () => {
                try {
                  await api.delete(`/admin/occasions/${o.slug}`);
                  toast.success("Occasion supprimée");
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                }
              }}
            />
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-10" data-testid="occasions-empty">
            <p className="text-ivory/60 mb-5">Aucune occasion pour le moment.</p>
            <button
              data-testid="occasions-seed-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/occasions/seed-defaults");
                  toast.success(`${plur(data.created, "occasion")} par défaut ${data.created > 1 ? "installées" : "installée"}`);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                }
              }}
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
              <Plus className="w-4 h-4" /> Installer les 8 occasions par défaut
            </button>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing?._isNew ? "Nouvelle occasion" : `Modifier — ${editing?.slug}`}</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              La page regroupe les produits dont le nom, la catégorie ou la sous-catégorie contient un des mots-clés.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <OccField label="Slug (URL — ex. tournoi-hockey)" placeholder="tournoi-hockey" readOnly={!editing._isNew} {...fieldProps("slug")} />
              {editing._isNew
                ? null
                : <p className="text-[11px] text-ivory/55 -mt-3">Le slug n'est pas modifiable (liens SEO existants).</p>}
              <div className="grid sm:grid-cols-2 gap-4">
                <OccField label="Titre (FR)" {...fieldProps("title_fr")} />
                <OccField label="Titre (EN)" {...fieldProps("title_en")} />
              </div>
              <OccField label="Description (FR) — affichée en haut de la page" textarea {...fieldProps("desc_fr")} />
              <OccField label="Description (EN)" textarea {...fieldProps("desc_en")} />
              <div className="grid sm:grid-cols-2 gap-4">
                <OccField label="Meta description SEO (FR, ~155 car.)" {...fieldProps("meta_fr")} />
                <OccField label="Meta description SEO (EN)" {...fieldProps("meta_en")} />
              </div>
              <OccField label="Mots-clés de correspondance (séparés par des virgules, nombre illimité)" placeholder="hockey, ringuette" {...fieldProps("keywords")} />
              <OccField label="Mots-clés SEO de la page (FR) — en tête des mots-clés diffusés aux moteurs" placeholder="trophées tournoi de hockey, médailles hockey personnalisées…" {...fieldProps("seo_keywords")} />
              <OccField label="Mots-clés SEO de la page (EN)" placeholder="hockey tournament trophies, personalized hockey medals…" {...fieldProps("seo_keywords_en")} />
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Sport (optionnel — alimente la page automatiquement via le filtre sport, tous catalogues confondus)</label>
                <select value={editing.sport || ""} onChange={(e) => setEditing((s) => ({ ...s, sport: e.target.value }))}
                  data-testid="occasion-form-sport"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none">
                  <option value="">Aucun — mots-clés seulement</option>
                  {SPORTS.map((s) => <option key={s.id} value={s.id} data-testid={`occasion-form-sport-${s.id}`}>{s.fr}</option>)}
                </select>
              </div>
              <OccField label="Filtre catégorie (optionnel — nom exact, ex. Médailles)" {...fieldProps("category")} />
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Image d'en-tête</label>
                <div className="flex items-center gap-3">
                  <input value={editing.image} onChange={(e) => setEditing((s) => ({ ...s, image: e.target.value }))}
                    data-testid="occasion-form-image" placeholder="/api/files/… ou URL"
                    className="flex-1 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                  <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} />
                  <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}
                    data-testid="occasion-form-upload"
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-3 rounded-lg text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Téléverser
                  </button>
                </div>
                {editing.image && <img src={editing.image} alt="" className="mt-3 h-20 object-contain bg-white rounded-lg p-1" />}
              </div>
              <div className="flex items-center justify-between pt-2">
                <label className="inline-flex items-center gap-3 text-sm text-ivory/70">
                  <Switch checked={!!editing.published} onCheckedChange={(v) => setEditing((s) => ({ ...s, published: v }))}
                    data-testid="occasion-form-published" />
                  Publiée (visible sur le site et dans le sitemap)
                </label>
                <button onClick={save} disabled={saving || !editing.slug.trim() || !editing.title_fr.trim()}
                  data-testid="occasion-form-save"
                  className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
